import { Button } from '@/components/ui/button';
import { ArrowDownIcon, BuildingIcon, CheckIcon, UserIcon } from '@/components/ui/icons';
import { MOTION } from '@/utils';
import {
  CABINET_PLAN_FEATURES,
  COLLAB_PLAN_FEATURES,
  PRICING_PLANS,
} from '@/utils/data/princingFunctions';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { useRef } from 'react';

const cabinetPlan = PRICING_PLANS.find((p) => p.id === 'cabinet')!;
const collaborateurPlan = PRICING_PLANS.find((p) => p.id === 'collaborateur')!;

export default function Plans() {
  const refs = {
    section: useRef<HTMLElement>(null),
    cabinet: useRef<HTMLDivElement>(null),
    collab: useRef<HTMLDivElement>(null),
  };

  useGSAP(
    () => {
      if (!refs.section.current) return;
      gsap.set([refs.cabinet.current, refs.collab.current], { autoAlpha: 0, y: MOTION.y });

      gsap
        .timeline({
          defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
          scrollTrigger: {
            trigger: refs.section.current,
            start: 'top 65%',
            toggleActions: 'play none none reverse',
          },
        })
        .to(refs.cabinet.current, { autoAlpha: 1, y: 0 })
        .to(refs.collab.current, { autoAlpha: 1, y: 0 }, '<+0.15');
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-white-section max-width-white-section-content grid grid-cols-1 gap-6 md:grid-cols-2"
      id="tarif-plans"
    >
      {/* Cabinet */}
      <div
        ref={refs.cabinet}
        className="border-dark-blue/8 flex flex-col gap-8 rounded-(--radius-current) border bg-white p-8 shadow-(--shadow-current) lg:p-10"
      >
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <div className="bg-orange rounded-md p-2 text-white">
              <BuildingIcon />
            </div>
            <div>
              <span className="text-dark-blue/40 font-roboto-medium block text-xs tracking-wide uppercase">
                zLawyer Cabinet
              </span>
              <span className="text-dark-blue font-roboto-medium text-sm">
                Pour les cabinets d&apos;avocats
              </span>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <span
              className="text-orange font-roboto-bold leading-none tabular-nums"
              style={{ fontSize: 'clamp(48px, 5vw, 64px)' }}
            >
              {cabinetPlan.price}€
            </span>
            <span className="text-dark-blue/40 mb-1.5 text-sm leading-snug">
              /mois
              <br />
              /utilisateur
            </span>
          </div>

          <ul className="flex flex-col gap-2">
            {CABINET_PLAN_FEATURES.slice(0, 4).map((f) => (
              <li key={f} className="flex items-center gap-2.5">
                <CheckIcon />
                <span className="text-dark-blue/70 text-sm">{f}</span>
              </li>
            ))}
          </ul>

          <a
            className="text-orange font-roboto-medium flex items-end gap-1 text-sm underline-offset-2 hover:underline"
            href="#tarif-comparaison"
          >
            Voir le tableau comparatif complet
            <ArrowDownIcon />
          </a>
        </div>

        <div className="mt-auto flex flex-col gap-3">
          <p className="text-dark-blue/40 text-xs!">
            Accès complet pour tous les membres du cabinet. Sans engagement.
          </p>
          <Button className="whitespace-nowrap" href="/contact" variant="fill">
            Demandez une démo
          </Button>
        </div>
      </div>

      {/* Collaborateur */}
      <div
        ref={refs.collab}
        className="border-dark-blue/6 bg-light-blue/60 flex flex-col gap-8 rounded-(--radius-current) border p-8 lg:p-10"
      >
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <div className="text-orange border-orange rounded-md border bg-white p-2">
              <UserIcon />
            </div>
            <div>
              <span className="text-dark-blue/40 font-roboto-medium block text-xs tracking-wide uppercase">
                zLawyer Collaborateur
              </span>
              <span className="text-dark-blue font-roboto-medium text-sm">
                Pour les collaborateurs indépendants
              </span>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <span
              className="text-dark-blue/80 font-roboto-bold leading-none tabular-nums"
              style={{ fontSize: 'clamp(48px, 5vw, 64px)' }}
            >
              {collaborateurPlan.price}€
            </span>
            <span className="text-dark-blue/40 mb-1.5 text-sm">/mois</span>
          </div>

          <div className="border-dark-blue/10 rounded-md border bg-white/60 px-3 py-2">
            <p className="text-dark-blue/60 text-xs! leading-relaxed">
              Réservé aux collaborateurs déjà utilisateurs de zLawyer via leur cabinet.
            </p>
          </div>

          <ul className="flex flex-col gap-2">
            {COLLAB_PLAN_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2.5">
                <CheckIcon />
                <span className="text-dark-blue/70 text-sm">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto flex flex-col gap-3">
          <p className="text-dark-blue/40 text-xs!">Sans engagement.</p>
          <Button
            className="whitespace-nowrap"
            href="/abonnement-collaborateur"
            variant="transparent"
          >
            Je souhaite m&apos;abonner
          </Button>
        </div>
      </div>
    </section>
  );
}
